import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, PieChart } from 'lucide-react';
import { fmtUSD } from './utils';
import logger from '../../utils/logger';

export default function BudgetsTab({ api }) {
  const [data, setData] = useState({ budgets: [], fiscal_year: new Date().getFullYear() });
  const [year, setYear] = useState(new Date().getFullYear());
  const [config, setConfig] = useState({ budget_categories: [] });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: '', fiscal_year: year, amount: '', notes: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([api.get(`/finance/budgets?fiscal_year=${year}`), api.get('/finance/config')]);
      setData(b.data); setConfig(c.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api, year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm({ category: (config.budget_categories || [])[0] || '', fiscal_year: year, amount: '', notes: '' }); setDialog(true); };
  const openEdit = (b) => { setEditing(b); setForm({ category: b.category, fiscal_year: b.fiscal_year, amount: b.amount, notes: b.notes || '' }); setDialog(true); };

  const save = async () => {
    try {
      if (!form.category) { toast.error('Category required'); return; }
      if (!form.amount) { toast.error('Amount required'); return; }
      if (editing) { await api.put(`/finance/budgets/${editing.id}`, form); toast.success('Updated'); }
      else { await api.post('/finance/budgets', form); toast.success('Saved'); }
      setDialog(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this budget line?')) return;
    await api.delete(`/finance/budgets/${id}`); fetchAll();
  };

  const totalBudget = data.budgets.reduce((s, b) => s + (b.amount || 0), 0);
  const totalSpent = data.budgets.reduce((s, b) => s + (b.spent || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm">Fiscal Year</Label>
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="w-32 border-[#DED8EB]" data-testid="budget-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[0, -1, -2, 1].map(o => { const y = new Date().getFullYear() + o; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}</SelectContent>
          </Select>
        </div>
        <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-9" data-testid="budget-add-btn"><Plus className="h-4 w-4" /> Add Budget Line</Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="border-[#DED8EB] bg-white"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#8E85A0] font-semibold">Total Budget</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totalBudget)}</p></CardContent></Card>
        <Card className="border-[#FFB74D] bg-[#FFF3E0]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#E65100] font-semibold">Spent</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totalSpent)}</p></CardContent></Card>
        <Card className="border-[#81C784] bg-[#E8F5E9]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#1B5E20] font-semibold">Remaining</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totalBudget - totalSpent)}</p></CardContent></Card>
      </div>

      {loading ? <p className="text-sm text-[#8E85A0]">Loading…</p> : data.budgets.length === 0 ? (
        <div className="text-center py-10 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg">
          <PieChart className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No budget lines for {year}. Add one to start tracking.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="budget-list">
          {data.budgets.map(b => {
            const pctColor = b.pct_used > 100 ? '#D9534F' : b.pct_used > 80 ? '#E65100' : '#2E7D32';
            return (
              <Card key={b.id} className="border shadow-sm bg-white" data-testid={`budget-row-${b.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-[#2D1B4E]">{b.category}</h3>
                        <span className="text-[11px] font-mono text-[#8E85A0]">{fmtUSD(b.spent)} / {fmtUSD(b.amount)}</span>
                        <span className="text-[11px] font-mono font-semibold" style={{ color: pctColor }}>{b.pct_used}%</span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-[#F0EDF6] overflow-hidden mt-1.5">
                        <div className="h-full transition-all" style={{ width: `${Math.min(100, b.pct_used || 0)}%`, backgroundColor: pctColor }} />
                      </div>
                      <p className="text-[10px] text-[#8E85A0] mt-1">{fmtUSD(b.remaining)} remaining</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(b)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`budget-edit-${b.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(b.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`budget-del-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="budget-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold text-[#2D1B4E]">{editing ? 'Edit Budget' : 'New Budget Line'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Category *</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="border-[#DED8EB]" data-testid="budget-cat"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(config.budget_categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fiscal Year</Label><Input type="number" value={form.fiscal_year} onChange={e => setForm(f => ({ ...f, fiscal_year: parseInt(e.target.value) || year }))} className="border-[#DED8EB]" data-testid="budget-fy" /></div>
              <div><Label>Amount ($)</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="border-[#DED8EB]" data-testid="budget-amt" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="border-[#DED8EB]" data-testid="budget-notes" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={save} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="budget-save-btn">{editing ? 'Update' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
